import type {
  ComponentPropsWithRef,
  ElementType,
  ReactElement,
  ReactNode,
} from "react";
import type { DropdownProps } from "../Dropdown";

export type IMenuOption = {
  id: string;
  text: string;
  icon?: ReactNode;
  accessory?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  options?: IMenuItem[];
  className?: string;
  dataAttributes?: Record<string, string>;
};

export type IMenuDivider = {
  type: "divider";
  className?: string;
};

export type IMenuHeader = {
  type: "header";
  text: string;
  className?: string;
};

export type IMenuCustomElement = {
  type: "element";
  element: ReactElement;
};

export type IMenuItem =
  | IMenuOption
  | IMenuDivider
  | IMenuHeader
  | IMenuCustomElement;

export type IMenuProps = Omit<
  DropdownProps,
  "interactions" | "role" | "trigger" | "children" | "content"
> & {
  trigger?: ReactElement<ComponentPropsWithRef<ElementType>>;
  children: ReactElement<ComponentPropsWithRef<ElementType>>;
  options: IMenuItem[];
  onSelect: (id: string) => void;
  preventDefaultOnTrigger?: DropdownProps["preventDefaultOnTrigger"];
  stopPropagationOnTrigger?: DropdownProps["stopPropagationOnTrigger"];
};

export const isMenuOption = (item: IMenuItem): item is IMenuOption => {
  return !("type" in item);
};

export const isMenuDivider = (item: IMenuItem): item is IMenuDivider => {
  return "type" in item && item.type === "divider";
};

export const isMenuHeader = (item: IMenuItem): item is IMenuHeader => {
  return "type" in item && item.type === "header";
};

export const isMenuCustomElement = (
  item: IMenuItem,
): item is IMenuCustomElement => {
  return "type" in item && item.type === "element";
};
